import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import * as m from '../../../../localization/out/messages.js';
import { subscribeJobApplicationsByUser, updateJobApplicationStatus } from '../../../api/job-application.js';
import { JobApplicationStatus } from '../../../db/job-application.js';
import { JobPosition } from '../../../db/job-position.js';
import { getJobPositionByID } from '../../../api/job-position.js';

export const CandidateJobPostingOverview = stateful(() => {
  const userObs = useObservable(() => currentUser<User>(), []);

  const applications = useQuery(() => {
    if (!userObs?._id) return of([]);
    return subscribeJobApplicationsByUser(userObs._id)({
      _id: 1,
      status: 1,
      jobPositionId: 1,
      createdAt: 1,
      interviewStart: 1,
    });
  }, [userObs?._id]);

  const jobPositionsById = useQuery(async () => {
    const list = applications.value ?? [];
    const ids = Array.from(new Set(list.map(a => a.jobPositionId).filter(Boolean)));
    const results = await Promise.all(
      ids.map(id =>
        getJobPositionByID(id!)({ _id: 1, title: 1, location: 1, company: { name: 1 }, employmentType: 1 }),
      ),
    );
    const map: Record<string, JobPosition> = {};
    for (const jp of results) if (jp?._id) map[jp._id] = jp as JobPosition;
    return map;
  }, [JSON.stringify((applications.value ?? []).map(a => a.jobPositionId).sort())]);

  const onCancel = async (application?: { _id: string }) => {
    if (!application) return;
    const ok = await XNativeDialog.confirm(m.candidate_cancel_application_confirm());
    if (!ok) return;
    try {
      // send company notification mails and remove application
      await updateJobApplicationStatus(application._id, JobApplicationStatus.REJECTED_BY_USER)();
    } catch (e) {
      console.error(e);
      await XSnackbar.show(m.candidate_cancel_application_error());
    }
  };

  if (applications.loading) {
    return html`<div class="p-8 flex justify-center items-center">
      <span class="loading loading-spinner loading-lg text-primary"></span>
    </div>`;
  }

  const apps = applications.value ?? [];

  if (!apps.length) {
    return html`
      <div class="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-300">
        <div class="card-body p-6">
          <h2 class="card-title text-xl font-bold mb-2">${m.candidate_applications_title()}</h2>
          <p class="text-base-content/70 mb-4">${m.candidate_no_applications_text()}</p>
          <div class="card-actions justify-end">
            <a class="btn btn-primary" href="/embed">${m.candidate_apply_now_button()}</a>
          </div>
        </div>
      </div>
    `;
  }

  // map status codes to translated labels
  const statusLabel = (status: string) => {
    switch (status) {
      case 'ACCEPTED':
        return m.application_status_ACCEPTED();
      case 'BOOKED':
        return m.application_status_BOOKED();
      case 'PENDING':
        return m.application_status_PENDING();
      case 'REJECTED':
        return m.application_status_REJECTED();
      case 'WAITING':
        return m.application_status_WAITING();
      case 'REJECTED_BY_USER':
        return m.application_status_REJECTED_BY_USER();
      default:
        return status || m.application_status_UNKNOWN();
    }
  };

  return html`
    <div class="flex flex-col gap-4 sm:gap-6">
      ${apps.map(app => {
        const jp = app.jobPositionId ? jobPositionsById.value?.[app.jobPositionId] : null;
        const status = app.status ?? 'PENDING';
        const canBook = status === 'ACCEPTED';
        const isBooked = status === 'BOOKED';
        const canCancel =
          status === JobApplicationStatus.ACCEPTED ||
          status === JobApplicationStatus.PENDING ||
          status === JobApplicationStatus.BOOKED ||
          status === JobApplicationStatus.WAITING;
        const interviewDateStr = app.interviewStart ? format(new Date(app.interviewStart), 'dd.MM.yyyy HH:mm') : null;

        // Define status badge style based on status
        let badgeClass = 'badge text-xs';
        switch (status) {
          case 'ACCEPTED':
            badgeClass += ' badge-success text-success-content';
            break;
          case 'BOOKED':
            badgeClass += ' badge-info text-info-content';
            break;
          case 'PENDING':
            badgeClass += ' badge-warning text-warning-content';
            break;
          default:
            badgeClass += ' badge-outline';
        }

        return html`
          <div class="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
            <div class="card-body p-3 sm:p-6">
              <div class="flex flex-col gap-2 sm:gap-3">
                <!-- Header with status badge and cancel button -->
                <div class="flex justify-between items-start">
                  <div>
                    <span class="${badgeClass} py-1 px-2">${statusLabel(status)}</span>
                  </div>

                  ${
                    canCancel
                      ? html`<button class="btn btn-outline btn-error btn-sm px-3" @click=${() => onCancel(app)}>
                          <span class="icon-[material-symbols--close] w-4 h-4 mr-1"></span>
                          ${m.candidate_cancel_button()}
                        </button>`
                      : nothing
                  }
                </div>
                <div>
                  <!-- Job details section -->
                  <div class="flex flex-col gap-1.5">
                    <a href="/embed/job/${app.jobPositionId}" class="link link-hover">
                      <h3 class="text-base sm:text-lg font-bold leading-tight">
                        ${jp?.title ?? m.job_title_fallback()}
                      </h3>
                    </a>

                    <!-- Company and location info -->
                    <div class="text-base-content/70 text-xs sm:text-sm">
                      ${jp?.company?.name ?? ''}${jp?.location ? ' • ' + jp.location : ''}
                    </div>

                    <!-- Application date -->
                    ${
                      app.createdAt
                        ? html`
                            <div class="text-xs text-base-content/60 mt-1">
                              ${m.candidate_application_submitted_label()}
                              ${format(new Date(app.createdAt), 'dd.MM.yyyy')}
                            </div>
                          `
                        : nothing
                    }
                  </div>
                </div>

                <div class="flex flex-wrap gap-2 mt-1">
                  ${
                    isBooked && interviewDateStr
                      ? html`<div
                          class="bg-primary text-primary-content rounded-md py-1.5 px-2 flex items-center w-full sm:w-auto justify-start"
                        >
                          <span class="icon-[material-symbols--calendar-month] w-4 h-4 mr-1 flex-shrink-0"></span>
                          <span class="text-xs whitespace-normal text-left">${interviewDateStr}</span>
                        </div>`
                      : nothing
                  }
                  <div class="flex flex-wrap gap-2 w-full">
                    ${
                      canBook
                        ? html`<a
                            class="btn btn-primary btn-sm h-8 min-h-0 flex-1 justify-center"
                            href="/book-appointment/${app.jobPositionId}"
                          >
                            <span class="icon-[material-symbols--calendar-month] w-4 h-4 mr-1 flex-shrink-0"></span>
                            <span class="text-xs">${m.candidate_book_appointment_button()}</span>
                          </a>`
                        : null
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
      })}
    </div>
  `;
});
